import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import '../../styles/Home.css';

interface NavSubItem {
    name: string;
    href: string;
    soon?: boolean;
    highlight?: boolean;
}

interface NavGroup {
    title: string;
    items: NavSubItem[];
}

type MobileRow = { heading: string } | NavSubItem;

interface NavItem {
    name: string;
    key: string;
    href: string;
    dropdown?: NavSubItem[];
    groups?: NavGroup[];
    featured?: boolean;
    soon?: boolean;
}

function SoonTag() {
    return (
        <sup className="ml-0.5 text-[7px] leading-none font-poppins font-bold uppercase tracking-wider text-charcoal/70">
            Soon
        </sup>
    );
}

export default function SecondaryNavbar() {
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeMobileSubmenu, setActiveMobileSubmenu] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const pressRef = useRef({ touch: false, wasOpen: false });
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const location = useLocation();
    const { t } = useTranslation();

    const openDropdown = (key: string) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setActiveDropdown(key);
    };

    // A short delay keeps the panel open while the pointer crosses from the name into it.
    const closeDropdownSoon = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setActiveDropdown(null), 150);
    };

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menus on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setActiveMobileSubmenu(null);
        setActiveDropdown(null);
    }, [location.pathname]);

    const bhairavGroups: NavGroup[] = [
        {
            title: 'Start here',
            items: [
                { name: t('nav.lifePurposeSadhana'), href: '/bhairav/life-purpose', highlight: true },
                { name: t('nav.forms'), href: '/bhairav/forms' },
                { name: 'Bhairava Quiz', href: '/quiz' },
            ],
        },
        {
            title: 'Practice',
            items: [
                { name: t('nav.naamJapa'), href: '/bhairav/naam-japa' },
                { name: t('nav.basicVidhi'), href: '/bhairav/vidhi' },
                { name: t('nav.stotras'), href: '/bhairav/stotras' },
                { name: 'Sadhana', href: '/bhairav/sadhana' },
            ],
        },
        {
            title: 'Learn',
            items: [
                { name: t('nav.tattva'), href: '/bhairav/tattva' },
                { name: t('nav.shastraStories'), href: '/bhairav/shastra-stories' },
                { name: t('nav.articles'), href: '/bhairav/articles' },
                { name: t('nav.vbt'), href: '/bhairav/vbt' },
                { name: t('nav.books'), href: '/bhairav/books' },
            ],
        },
        {
            title: 'Explore',
            items: [
                { name: 'Films', href: '/films' },
                { name: t('nav.places'), href: '/bhairav/pilgrimage-places' },
                { name: t('nav.images'), href: '/bhairav/images', soon: true },
            ],
        },
    ];

    const shivGroups: NavGroup[] = [
        {
            title: 'Scriptures',
            items: [
                { name: 'Shiva Purana', href: '/shiv/shiva-purana' },
                { name: 'Rudrashtadhyayi', href: '/shiv/rudrashtadhyayi' },
                { name: 'Siva Sutras', href: '/shiv/siva-sutras' },
                { name: 'Spanda Karikas', href: '/shiv/spanda-karikas' },
                { name: 'Isvara Gita', href: '/shiv/isvara-gita' },
            ],
        },
        {
            title: 'Topics',
            items: [
                { name: 'Jyotirlingas', href: '/shiv/jyotirlingas' },
                { name: 'Topics', href: '/shiv/topics' },
                { name: 'Sampradayas', href: '/shiv/sampradaya' },
                { name: 'Linga Symbolism', href: '/shiv/linga-symbolism' },
            ],
        },
        {
            title: 'Practice',
            items: [
                { name: t('nav.stotras'), href: '/shiv/stotras' },
                { name: t('nav.basicVidhi'), href: '/shiv/vidhi' },
                { name: t('nav.forms'), href: '/shiv/forms' },
            ],
        },
        {
            title: 'Coming soon',
            items: [
                { name: t('nav.tattva'), href: '/shiv/tattva', soon: true },
                { name: t('nav.articles'), href: '/shiv/articles', soon: true },
            ],
        },
    ];

    const navItems: NavItem[] = [
        {
            name: t('nav.bhairav'),
            key: 'bhairav',
            href: '/bhairav',
            featured: true,
            groups: bhairavGroups,
            dropdown: bhairavGroups.flatMap((group) => group.items),
        },
        {
            name: t('nav.shiv'),
            key: 'shiv',
            href: '/shiv',
            featured: true,
            groups: shivGroups,
            dropdown: shivGroups.flatMap((group) => group.items),
        },
        {
            name: t('nav.guru'),
            key: 'guru',
            href: '/guru',
            soon: true,
            dropdown: [
                { name: t('nav.forms'), href: '/guru/forms', soon: true },
                { name: t('nav.stotras'), href: '/guru/stotras', soon: true },
                { name: t('nav.articles'), href: '/guru/articles', soon: true },
            ],
        },
        {
            name: t('nav.maa'),
            key: 'maa',
            href: '/maa',
            soon: true,
            dropdown: [
                { name: t('nav.forms'), href: '/maa/forms', soon: true },
                { name: t('nav.stotras'), href: '/maa/stotras', soon: true },
                { name: t('nav.articles'), href: '/maa/articles', soon: true },
            ],
        },
        {
            name: t('nav.vishnu'),
            key: 'vishnu',
            href: '/vishnu',
            soon: true,
            dropdown: [
                { name: t('nav.forms'), href: '/vishnu/forms', soon: true },
                { name: t('nav.stotras'), href: '/vishnu/stotras', soon: true },
                { name: t('nav.articles'), href: '/vishnu/articles', soon: true },
            ],
        },
        { name: t('nav.resources'), key: 'resources', href: '/resources' },
        { name: t('nav.aboutUs'), key: 'aboutUs', href: '/about' },
        { name: t('nav.contact'), key: 'contact', href: '/contact' },
    ];

    const renderDesktopLink = (subItem: NavSubItem, delayIndex: number) => (
        <motion.div
            key={subItem.name}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delayIndex * 0.04 }}
        >
            <Link
                to={subItem.href}
                className={`group/sub block px-5 py-2.5 text-sm font-noto-serif transition-colors duration-200 relative ${
                        subItem.highlight ? 'font-bold' : 'font-medium'
                    } ${subItem.soon ? 'opacity-70 hover:opacity-100' : ''}`}
                style={{
                    color: location.pathname === subItem.href ? '#8B6914' : '#8B6914',
                    borderLeft: location.pathname === subItem.href
                        ? '3px solid #b8860b'
                        : '3px solid transparent',
                    background: location.pathname === subItem.href
                        ? 'linear-gradient(90deg, rgba(184,134,11,0.08), transparent)'
                        : subItem.highlight
                    ? 'rgba(184,134,11,0.10)'
                    : 'transparent',
                }}
            >
                <span className="relative z-10">
                    {subItem.name}
                    {subItem.soon && <SoonTag />}
                </span>

                {/* Hover gradient underline for submenu */}
                <span
                    className="absolute bottom-1 left-5 right-5 h-[1.5px] rounded-full scale-x-0 group-hover/sub:scale-x-100 transition-transform duration-300 origin-center"
                    style={{ background: 'linear-gradient(90deg, #b8860b, #7c3aed, #db2777)' }}
                />

                {/* Hover background */}
                <span className="absolute inset-0 opacity-0 group-hover/sub:opacity-100 transition-opacity duration-200"
                    style={{ background: 'linear-gradient(90deg, rgba(184,134,11,0.06), rgba(124,58,237,0.04), transparent)' }}
                />

                {/* Active dot */}
                {location.pathname === subItem.href && (
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
                        style={{ background: 'linear-gradient(135deg, #b8860b, #7c3aed)' }}
                    />
                )}
            </Link>
        </motion.div>
    );

    const mobileRows = (item: NavItem): MobileRow[] =>
        item.groups
            ? item.groups.flatMap((group): MobileRow[] => [{ heading: group.title }, ...group.items])
            : item.dropdown ?? [];

    // Check if a nav item is active
    const isActive = (href: string) => location.pathname.startsWith(href);

    return (
        <>
            {/* ============== DESKTOP NAVIGATION ============== */}
            <nav
                role="navigation"
                aria-label="Main navigation"
                className={`hidden md:block fixed top-16 md:top-20 left-0 right-0 z-40 transition-shadow duration-500 bg-white ${
                    scrolled ? 'shadow-md' : 'shadow-sm'
                }`}
                style={{ borderBottom: '1px solid #1a1a2e' }}
            >
                {/* Floating spiritual symbols at edges */}
                <div className="navbar-edge-symbols navbar-edge-symbols-left">
                    <span className="edge-symbol" style={{ left: '25%', top: '20%', fontSize: 13, color: '#b8860b', animationDelay: '0.5s' }}>ॐ</span>
                    <span className="edge-symbol" style={{ left: '50%', top: '55%', animationDelay: '2.2s' }}>
                        <svg width="13" height="17" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#7c3aed" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span className="edge-symbol" style={{ left: '35%', top: '75%', animationDelay: '4s' }}>
                        <svg width="11" height="14" viewBox="0 0 18 22" fill="none"><path d="M3 2L15 2L9 11L15 20L3 20L9 11Z" stroke="#db2777" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                    </span>
                </div>
                <div className="navbar-edge-symbols navbar-edge-symbols-right">
                    <span className="edge-symbol" style={{ right: '30%', top: '25%', animationDelay: '1.2s' }}>
                        <svg width="13" height="17" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#b8860b" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span className="edge-symbol" style={{ right: '55%', top: '50%', fontSize: 13, color: '#7c3aed', animationDelay: '3s' }}>ॐ</span>
                    <span className="edge-symbol" style={{ right: '25%', top: '70%', animationDelay: '4.5s' }}>
                        <svg width="11" height="14" viewBox="0 0 18 22" fill="none"><path d="M3 2L15 2L9 11L15 20L3 20L9 11Z" stroke="#db2777" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                    </span>
                </div>

                <div className="relative z-[2] max-w-7xl mx-auto px-6">
                    <div className="flex items-center justify-center space-x-1 py-2.5">
                        {navItems.map((item, index) => (
                            <motion.div
                                key={item.key}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05, duration: 0.3 }}
                                className="relative group"
                                onMouseEnter={() => openDropdown(item.key)}
                                onMouseLeave={closeDropdownSoon}
                                onFocus={() => openDropdown(item.key)}
                                onBlur={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                                        setActiveDropdown(null);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setActiveDropdown(null);
                                }}
                            >
                                <Link
                                    to={item.href}
                                    onPointerDown={(e) => {
                                        pressRef.current = { touch: e.pointerType !== 'mouse', wasOpen: activeDropdown === item.key };
                                    }}
                                    onClick={(e) => {
                                        // On touch screens there is no hover: the first tap opens the
                                        // menu, a second tap on the same item goes to its page.
                                        const press = pressRef.current;
                                        pressRef.current = { touch: false, wasOpen: false };
                                        if (item.dropdown && press.touch && !press.wasOpen) {
                                            e.preventDefault();
                                            setActiveDropdown(item.key);
                                        }
                                    }}
                                    aria-haspopup={item.dropdown ? 'true' : undefined}
                                    aria-expanded={item.dropdown ? activeDropdown === item.key : undefined}
                                    className={`relative px-4 py-2.5 rounded-lg font-poppins font-semibold transition-colors duration-300 inline-block ${
                                        item.featured ? 'text-base font-bold' : 'text-sm'
                                    } ${item.soon ? 'opacity-70 hover:opacity-100' : ''}`}
                                >
                                    <span className="relative z-10 nav-golden-text">{item.name}</span>
                                    {item.soon && <SoonTag />}

                                    {/* Active bottom indicator */}
                                    {isActive(item.href) && (
                                        <motion.div
                                            layoutId="activeNavTab"
                                            className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                                            style={{ background: 'linear-gradient(90deg, #b8860b, #7c3aed, #db2777)' }}
                                            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}

                                    {/* Hover gradient underline — grows from center */}
                                    <span
                                        className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center"
                                        style={{ background: 'linear-gradient(90deg, #b8860b, #7c3aed, #db2777)' }}
                                    />

                                    {/* Hover text color shift */}
                                    <span
                                        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.06), rgba(124,58,237,0.06))' }}
                                    />
                                </Link>

                                {/* ===== Dropdown ===== */}
                                <AnimatePresence>
                                    {item.dropdown && activeDropdown === item.key && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10, scale: 0.97 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: -10, scale: 0.97 }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                            className={`absolute top-full left-0 rounded-xl overflow-hidden shadow-xl ${item.groups ? 'w-[30rem]' : 'w-56'}`}
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.92)',
                                                backdropFilter: 'blur(16px)',
                                                border: '2px solid transparent',
                                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), linear-gradient(135deg, #b8860b, #7c3aed, #db2777)',
                                                backgroundOrigin: 'border-box',
                                                backgroundClip: 'padding-box, border-box',
                                            }}
                                        >
                                            <div className="py-1">
                                                {item.groups ? (
                                                    <div className="columns-2 gap-x-1 px-1">
                                                        {item.groups.map((group, groupIndex) => (
                                                            <div key={group.title} className="break-inside-avoid pb-2">
                                                                <p className="px-5 pt-2 pb-1 text-[11px] font-poppins font-semibold uppercase tracking-wider text-charcoal/50">
                                                                    {group.title}
                                                                </p>
                                                                {group.items.map((subItem, subIndex) => renderDesktopLink(subItem, groupIndex * 3 + subIndex))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    item.dropdown.map((subItem, subIndex) => renderDesktopLink(subItem, subIndex))
                                                )}
                                            </div>

                                            {/* Bottom gradient line */}
                                            <div className="h-0.5" style={{ background: 'linear-gradient(90deg, transparent, #b8860b, #7c3aed, #db2777, transparent)' }} />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </nav>

            {/* ============== MOBILE NAVIGATION ============== */}
            <div
                className="md:hidden fixed top-16 left-0 right-0 z-40 bg-white transition-shadow duration-300"
                style={{ borderBottom: '1px solid #1a1a2e' }}
            >
                {/* Mobile floating spiritual symbols */}
                <div className="navbar-edge-symbols navbar-edge-symbols-left">
                    <span className="edge-symbol" style={{ left: '20%', top: '25%', fontSize: 12, color: '#b8860b', animationDelay: '0.3s' }}>ॐ</span>
                    <span className="edge-symbol" style={{ left: '50%', top: '60%', animationDelay: '2.5s' }}>
                        <svg width="11" height="14" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#7c3aed" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                </div>
                <div className="navbar-edge-symbols navbar-edge-symbols-right">
                    <span className="edge-symbol" style={{ right: '25%', top: '30%', animationDelay: '1.5s' }}>
                        <svg width="11" height="14" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#b8860b" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span className="edge-symbol" style={{ right: '50%', top: '65%', fontSize: 12, color: '#7c3aed', animationDelay: '3.5s' }}>ॐ</span>
                </div>
                <motion.button
                    onClick={() => {
                        if (!isMobileMenuOpen) setActiveMobileSubmenu('bhairav');
                        setIsMobileMenuOpen(!isMobileMenuOpen);
                    }}
                    whileTap={{ scale: 0.97 }}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                    aria-label="Toggle navigation menu"
                    className="relative z-[2] w-full px-6 py-3.5 flex items-center justify-between transition-colors"
                >
                    <span className="font-poppins font-semibold text-sm flex items-center">
                        <svg
                            className="w-5 h-5 mr-2"
                            style={{ color: '#7c3aed' }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                        <span className="nav-golden-text">{t('nav.menu')}</span>
                    </span>
                    <motion.svg
                        animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-5 h-5"
                        style={{ color: '#7c3aed' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </motion.svg>
                </motion.button>

                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            id="mobile-menu"
                            role="menu"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: 'easeInOut' }}
                            className="overflow-hidden"
                            style={{
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(16px)',
                            }}
                        >
                            <div className="max-h-[70vh] overflow-y-auto py-1">
                                {navItems.map((item, index) => (
                                    <motion.div
                                        key={item.key}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="border-b last:border-0"
                                        style={{ borderColor: 'rgba(26, 26, 46, 0.08)' }}
                                    >
                                        <div className="flex items-center group/mob">
                                            <Link
                                                to={item.href}
                                                onClick={() => !item.dropdown && setIsMobileMenuOpen(false)}
                                                className={`relative flex-1 px-6 py-3.5 font-poppins font-semibold transition-colors duration-300 ${
                                                    item.featured ? 'text-base font-bold' : 'text-sm'
                                                } ${item.soon ? 'opacity-70' : ''}`}
                                                style={{
                                                    borderLeft: isActive(item.href)
                                                        ? '3px solid #b8860b'
                                                        : '3px solid transparent',
                                                    background: isActive(item.href)
                                                        ? 'linear-gradient(90deg, rgba(184,134,11,0.06), transparent)'
                                                        : 'transparent',
                                                }}
                                            >
                                                <span className="nav-golden-text">{item.name}</span>
                                                {item.soon && <SoonTag />}
                                                {/* Gradient underline on active */}
                                                {isActive(item.href) && (
                                                    <span
                                                        className="absolute bottom-0 left-6 right-6 h-[1.5px] rounded-full"
                                                        style={{ background: 'linear-gradient(90deg, #b8860b, #7c3aed, #db2777)' }}
                                                    />
                                                )}
                                            </Link>
                                            {item.dropdown && (
                                                <motion.button
                                                    onClick={() =>
                                                        setActiveMobileSubmenu(
                                                            activeMobileSubmenu === item.key ? null : item.key
                                                        )
                                                    }
                                                    whileTap={{ scale: 0.95 }}
                                                    aria-label={`Show ${item.name} pages`}
                                                    aria-expanded={activeMobileSubmenu === item.key}
                                                    className="px-5 py-3.5"
                                                    style={{ color: '#7c3aed' }}
                                                >
                                                    <motion.svg
                                                        animate={{ rotate: activeMobileSubmenu === item.key ? 180 : 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </motion.svg>
                                                </motion.button>
                                            )}
                                        </div>

                                        {/* Mobile Submenu */}
                                        <AnimatePresence>
                                            {item.dropdown && activeMobileSubmenu === item.key && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    style={{ background: 'rgba(248, 248, 250, 0.8)' }}
                                                >
                                                    {mobileRows(item).map((row, subIndex) => 'heading' in row ? (
                                                        <p
                                                            key={`heading-${row.heading}`}
                                                            className="px-12 pt-3 pb-1 text-[11px] font-poppins font-semibold uppercase tracking-wider text-charcoal/50"
                                                        >
                                                            {row.heading}
                                                        </p>
                                                    ) : (
                                                        <motion.div
                                                            key={row.name}
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: subIndex * 0.04 }}
                                                        >
                                                            <Link
                                                                to={row.href}
                                                                onClick={() => setIsMobileMenuOpen(false)}
                                                                className={`relative group/msub block px-12 py-3 text-sm font-noto-serif font-medium transition-colors duration-200 ${
                                                                    row.soon ? 'opacity-70' : ''
                                                                }`}
                                                                style={{
                                                                    color: '#8B6914',
                                                                    borderLeft: location.pathname === row.href
                                                                        ? '3px solid #b8860b'
                                                                        : '3px solid transparent',
                                                                    background: location.pathname === row.href
                                                                        ? 'linear-gradient(90deg, rgba(184,134,11,0.08), transparent)'
                                                                        : 'transparent',
                                                                }}
                                                            >
                                                                {row.name}
                                                                {row.soon && <SoonTag />}
                                                                {/* Gradient underline on active submenu */}
                                                                {location.pathname === row.href && (
                                                                    <span
                                                                        className="absolute bottom-0 left-12 right-12 h-[1.5px] rounded-full"
                                                                        style={{ background: 'linear-gradient(90deg, #b8860b, #7c3aed, #db2777)' }}
                                                                    />
                                                                )}
                                                            </Link>
                                                        </motion.div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
}
